import React, { useEffect, useContext, useRef, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';

import { breakpoints } from '../../config/constants';
import deviceInfo from '../../helpers/deviceInfo';
import canUseDOM from '../../helpers/canUseDOM';
import { normalizeWheel } from '../../helpers/utils';
import useDebounce from '../../helpers/hooks/useDebounce';
import DecodeGlobalContext from '../../redux/decodeGlobalContext';
import ArticleCardsRenderer from '../../components/Cards/ArticleCardsRenderer';
import ArticleTransporter from '../../components/ArticleTransporter';
import Meta from '../../components/Meta';
import useArticleEntry from '../../hooks/useArticleEntry';

import './styles.scss';
import gtmPush from '../../helpers/gtmPush';
import ScrollDepthTracker from '../../components/ScrollDepthTracker';

const parseSocialMetaSection = (socialMetaSection, title) => {
  const meta = (socialMetaSection && socialMetaSection[0]) || {};
  const metaImage = (meta.metaImage && meta.metaImage[0]) || {};
  return {
    title: meta.metaTitle || title,
    description: meta.metaDescription,
    imageURL: metaImage.url,
    keywords: meta.metaKeywords
  };
};

const Article = () => {
  const location = useLocation();
  const history = useHistory();
  const { state, dispatch } = useContext(DecodeGlobalContext);
  const {
    decodeArticleCards: articleCards,
    title,
    category,
    decodeSecondaryCategory: secondaryCategory,
    postDate,
    loaded,
    socialMetaSection
  } = useArticleEntry();

  const { navPagination, isTransported } = state;
  const navPaginationRef = useRef(navPagination);
  const articlesRef = useRef();
  const transporterStartRef = useRef();
  const transporterEndRef = useRef();
  const scrollMoveRef = useRef(false);
  const scrollRAFRef = useRef(false);
  const timeoutRef = useRef(null);
  const srButtonRef = useRef(null);
  const mutationObserverRef = useRef();
  const debounce = useDebounce();
  const supportsNativeSmoothScroll =
    canUseDOM && 'scrollBehavior' in document.documentElement.style;
  const isMobileOrTablet = window.innerWidth < breakpoints.smallDesktop;
  const getTransporterWidth = () =>
    transporterStartRef.current ? transporterStartRef.current.offsetWidth : 0;

  /*
   * These scroll helpers/functions are based on
   * https://nicegist.github.io/d210786daa23fd57db59634dd231f341
   * Smooth scroll polyfill. Just modified some parts for the .articles element
   * side-scrolling and prevent errors related to .scrollTo incompatibilities.
   */
  const forceSmoothScrollTo = (to, duration) => {
    const element = articlesRef.current;
    const start = element.scrollLeft;
    const change = to - start;
    const startDate = Number(new Date());

    // t = current time
    // b = start value
    // c = change in value
    // d = duration
    const easeInOutQuad = (t, b, c, d) => {
      t /= d / 2;
      if (t < 1) return (c / 2) * t * t + b;
      t--;
      return (-c / 2) * (t * (t - 2) - 1) + b;
    };

    const animateScroll = () => {
      const currentDate = Number(new Date());
      const currentTime = currentDate - startDate;
      element.scrollLeft = parseInt(easeInOutQuad(currentTime, start, change, duration), 10);
      if (currentTime < duration) {
        window.requestAnimationFrame(animateScroll);
      } else {
        element.scrollLeft = to;
      }
    };

    animateScroll();
  };

  const nativeSmoothScrollTo = scrollValue => {
    articlesRef.current.scrollTo({
      left: scrollValue,
      top: 0,
      behavior: 'smooth'
    });
  };

  const scrollToCard = scrollXValue => {
    if (articlesRef.current) {
      if (supportsNativeSmoothScroll && !deviceInfo.isIOS) {
        nativeSmoothScrollTo(scrollXValue);
      } else {
        forceSmoothScrollTo(scrollXValue, 200);
      }
    }
  };

  const checkAndRedirectToHome = useCallback(() => {
    clearTimeout(timeoutRef.current);

    const isDesktop = window.innerWidth >= breakpoints.smallDesktop;
    const {
      scrollLeft,
      scrollWidth: currentScrollWidth,
      offsetWidth: currentOffsetWidth
    } = articlesRef.current;
    const currentScrollLeft = Math.ceil(scrollLeft);
    const scrollBounds = currentScrollWidth - currentOffsetWidth;
    const transporterStartWidth = transporterStartRef.current.offsetWidth;
    const transporterEndWidth = transporterEndRef.current.offsetWidth;

    // If the scroll reached the end, do not pull the scroll back for desktop.
    if (isDesktop && (currentScrollLeft === 0 || currentScrollLeft >= scrollBounds)) {
      return;
    }

    let scrollFactor = 0;

    // If scrolling to the left side
    if (currentScrollLeft < transporterStartWidth) {
      scrollFactor = transporterStartWidth;
    }

    // If scrolling to the right side
    if (
      currentScrollLeft > scrollBounds - transporterEndWidth &&
      currentScrollLeft < scrollBounds
    ) {
      scrollFactor = scrollBounds - transporterEndWidth;
    }

    if (scrollFactor > 0) {
      timeoutRef.current = setTimeout(() => {
        if (isDesktop) {
          scrollToCard(scrollFactor);
        }
      }, 250);
    }

    scrollRAFRef.current = false;
  }, [articlesRef, dispatch, timeoutRef, scrollRAFRef]);

  const updateCardIndecesAndPagination = useCallback(() => {
    const articleCardElements = document.getElementsByClassName('article-card-base');

    Array.from(articleCardElements).forEach((cardElement, index) => {
      cardElement.setAttribute('data-card-index', index + 1);
    });

    if (articleCardElements.length) {
      // Set the total of cards using the actual number of elements.
      // In mobile, some card components become two cards dynamically.
      dispatch({
        type: 'SET_NAVIGATION_PAGINATION',
        payload: {
          page: 1,
          total: articleCardElements.length
        }
      });
    }
  }, [navPagination]);

  const handleMainContentSroll = useCallback(() => {
    if (scrollRAFRef.current) return;

    scrollRAFRef.current = true;
    window.requestAnimationFrame(checkAndRedirectToHome);
  }, [scrollRAFRef, checkAndRedirectToHome]);

  function handleMouseWheelScroll(event) {
    event.preventDefault();

    const { pixelY } = normalizeWheel(event);

    window.requestAnimationFrame(() => {
      // Add the normalized delta to the Article scrollLeft value.
      // The value being multiplied is for increasing/decreasing speed.
      articlesRef.current.scrollLeft += pixelY * 0.5;
    });
  }

  function handleTouchEnd(event) {
    if (!scrollMoveRef.current) {
      const changedTouchEntry = event.changedTouches;

      if (changedTouchEntry.length) {
        const { page, total } = navPaginationRef.current;
        const { clientX, target } = changedTouchEntry[0];
        const excludedTags = ['A', 'BUTTON', 'svg', 'path', 'SPAN'];
        const isForwardScroll = clientX > window.innerWidth / 2;
        const isTapAllowed =
          ((isForwardScroll && page < total) || (!isForwardScroll && page > 1)) &&
          !target.classList.contains('article-transporter');

        if (
          isTapAllowed &&
          target &&
          !excludedTags.includes(target.tagName) &&
          !target.dataset.disableTouchEnd
        ) {
          const horizontalScrollValue = isForwardScroll
            ? page * window.innerWidth
            : (page - 2) * window.innerWidth;

          scrollToCard(horizontalScrollValue + getTransporterWidth());
        }
      }
    }

    return true;
  }

  useEffect(() => {
    const orientationChangeListener = () => {
      if (!articlesRef.current) return;

      const shouldResetPosition =
        window.matchMedia('(orientation: landscape)').matches &&
        window.innerWidth > breakpoints.tablet;
      const currentPage = navPaginationRef.current ? navPaginationRef.current.page - 1 : 1;

      articlesRef.current.scrollLeft = shouldResetPosition
        ? getTransporterWidth()
        : currentPage * window.innerWidth + getTransporterWidth();
    };

    if (isMobileOrTablet) {
      // Detect device screen orientation change.
      // Reset scroll position if the view is landscape and in small desktop view.
      window.addEventListener('orientationchange', debounce(orientationChangeListener, 100));
      return () => {
        window.removeEventListener('orientationchange', debounce(orientationChangeListener, 100));
      };
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      // Still need to check value here as there is still error when resizing.
      if (!articlesRef.current) return;

      articlesRef.current.style.height =
        window.innerHeight === document.body.offsetHeight ? `${window.innerHeight}px` : '';
    };

    if (articleCards.length && articlesRef.current) {
      if (!deviceInfo.isAndroid) {
        articlesRef.current.scrollLeft = getTransporterWidth();
      }

      articlesRef.current.addEventListener('scroll', handleMainContentSroll);

      // Bind the touch event handlers for mobile/tablet only.
      // For desktop, use onwheel.
      if (isMobileOrTablet) {
        mutationObserverRef.current = new MutationObserver(() => {
          updateCardIndecesAndPagination();
        });

        mutationObserverRef.current.observe(articlesRef.current, { childList: true });

        window.addEventListener('resize', handleResize);

        handleResize();

        articlesRef.current.addEventListener('touchstart', () => {
          scrollMoveRef.current = false;
        });
        articlesRef.current.addEventListener('touchmove', () => {
          scrollMoveRef.current = true;
        });
        articlesRef.current.addEventListener('touchend', handleTouchEnd);

        // Add a short timeout to update the data-card-index of Article Cards.
        setTimeout(() => {
          updateCardIndecesAndPagination();
        }, 100);
      } else {
        articlesRef.current.addEventListener('wheel', handleMouseWheelScroll);
      }

      dispatch({ type: 'SET_ARTICLE_CARDS_LOADED', payload: true });
    }

    return () => {
      // Clean up.
      if (articlesRef.current && articlesRef.current.length) {
        articlesRef.current.removeEventListener('scroll', handleMainContentSroll);

        if (isMobileOrTablet) {
          articlesRef.current.removeEventListener('touchend', handleTouchEnd);

          window.removeEventListener('resize', handleResize);
        } else {
          articlesRef.current.removeEventListener('wheel', handleMouseWheelScroll);
        }

        if (mutationObserverRef.current) {
          mutationObserverRef.current.disconnect();
        }

        window.cancelAnimationFrame(checkAndRedirectToHome);
        clearTimeout(timeoutRef.current);
      }
    };
  }, [articleCards]);

  useEffect(() => {
    // Initiate the GTM event once the page is loaded.
    if (loaded) {
      const categoryTitle = category ? category.map(cat => cat.title).join() : '';
      const secondaryCategoryTitle = secondaryCategory
        ? secondaryCategory.map(cat => cat.title).join()
        : '';
      gtmPush({
        event: 'gtm.messagePageView',
        contentType: 'Decode Article page',
        publishDate: postDate,
        category: categoryTitle,
        secondaryCategory: secondaryCategoryTitle
      });
    }
  }, [loaded]);

  useEffect(() => {
    // Update the navPaginationRef when global context/state changes.
    navPaginationRef.current = navPagination;
  }, [navPagination]);

  useEffect(() => {
    if (articlesRef.current) {
      const scrollLeftValue = deviceInfo.isIOS ? window.innerWidth : window.innerWidth * -1;
      const scrollLeft = isMobileOrTablet ? scrollLeftValue : window.innerHeight * 0.46;

      const focusable = articlesRef.current;
      const a11yFocus = srButtonRef.current;
      // This is to prevent the dialog from snapping to 0;
      focusable.scrollLeft = scrollLeft;
      a11yFocus.style.left = `${scrollLeft}px`;
      a11yFocus.focus();
    }
  }, [loaded]);

  return articleCards.length ? (
    <>
      <Meta {...parseSocialMetaSection(socialMetaSection, title)} active={true} />
      <CSSTransition
        in={!isTransported}
        timeout={300}
        classNames="articles"
        onExited={() => {
          setTimeout(() => {
            history.push('/decode');

            dispatch({ type: 'SET_NAVIGATION_MENU_HIDDEN', payload: false });
          }, 500);
        }}>
        <section role="dialog" className="articles" ref={articlesRef}>
          <ScrollDepthTracker
            pageName="DecodeArticles"
            pageURL={`${window.location.host}${location.pathname}`}
            scrollRef={articlesRef}
            // defined by css size of article transporter
            // scroll depth is equal to article transporter left & right width
            scrollDepthOffset={
              (isMobileOrTablet ? window.innerWidth * -1 : window.innerHeight * -0.46) * 2
            }
            // scroll left offset is equal to article transporter left width
            scrollLeftOffset={
              isMobileOrTablet ? window.innerWidth * -1 : window.innerHeight * -0.46
            }>
            <ArticleTransporter transporterRef={transporterStartRef} position="start" />
            <button ref={srButtonRef} className="sr__only" />
            <ArticleCardsRenderer articleCards={articleCards} />
            <ArticleTransporter transporterRef={transporterEndRef} position="end" />
          </ScrollDepthTracker>
        </section>
      </CSSTransition>
    </>
  ) : null;
};

export default Article;
